import { workspaceIdentityQuery, workspacePath } from './workspace-route-builders';
import { surfaceWorkspacePresentation } from './workspace-presentation';
import {
  RESERVED_ROOT_SEGMENTS,
  WORKSPACE_PREFIX_ALIASES,
  WORKSPACE_ROUTE_TEMPLATES,
} from './workspace-route-contract';

import type {
  WorkspaceDestination,
  WorkspacePathPresentation,
  WorkspacePresentation,
  WorkspaceRouteFamily,
  WorkspaceRouteTemplate,
} from './workspace-route-contract';

/**
 * The parse half of the route contract. Encoding policy, stated once:
 *
 * 1. A raw pathname carrying an ENCODED path separator (`%2F`, `%5C`, either case) or a literal
 *    backslash is rejected BEFORE anything is decoded. Decoding first would let
 *    `/people/a%2F..%2Fauth` become a two-segment path after the segment count was already checked.
 * 2. Dynamic segments are then decoded exactly once, so a destination always holds the DECODED
 *    identifier: `parseWorkspacePath('/ads/disclaimer/a%20b')` yields the key `a b`.
 * 3. `workspace-route-builders` re-encodes with exactly one pass, so parse and build accept the
 *    same values and `build(parse(p)) === p` for every path this module accepts.
 */
const ENCODED_SEPARATOR_PATTERN = /%2f|%5c/i;
const QUERY_OR_FRAGMENT_START = /[#?]/;
const TRAVERSAL_SEGMENTS: ReadonlySet<string> = new Set(['.', '..']);
/** Named once so the parse, render, and predicate sites of this family cannot drift apart. */
const TASK_DETAIL = 'task-detail';

export interface WorkspaceRouteMatch {
  readonly kind: 'workspace';
  readonly destination: WorkspaceDestination;
  readonly presentation: WorkspacePathPresentation;
}

export type WorkspacePathResult =
  | WorkspaceRouteMatch
  | { readonly kind: 'reserved' }
  | { readonly kind: 'unknown' };

const UNKNOWN: WorkspacePathResult = { kind: 'unknown' };
const RESERVED: WorkspacePathResult = { kind: 'reserved' };

function matched(
  destination: WorkspaceDestination,
  presentation: WorkspacePathPresentation,
): WorkspaceRouteMatch {
  return { kind: 'workspace', destination, presentation };
}

function isMatch(result: WorkspacePathResult): result is WorkspaceRouteMatch {
  return result.kind === 'workspace';
}

/**
 * Split a pathname into raw segments, or `null` when it violates the shape rules: it must be a
 * string starting with a single `/`, carry no duplicate slash, no backslash, no encoded separator,
 * and at most one trailing slash. `//host` is rejected here too — a protocol-relative URL parsed
 * loosely presents a plausible-looking pathname.
 */
function splitStrictPathname(pathname: string): readonly string[] | null {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) {
    return null;
  }
  if (
    pathname.includes('//') ||
    pathname.includes('\\') ||
    ENCODED_SEPARATOR_PATTERN.test(pathname)
  ) {
    return null;
  }
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed === '/' ? [] : trimmed.slice(1).split('/');
}

/** Traversal is checked on the DECODED value so `%2e%2e` and `..` reach the same verdict. */
function decodeSegment(segment: string): string | null {
  if (segment.length === 0) {
    return null;
  }
  try {
    const decoded = decodeURIComponent(segment);
    return decoded.length > 0 && !TRAVERSAL_SEGMENTS.has(decoded) ? decoded : null;
  } catch {
    return null;
  }
}

export interface RelativePathnameParts {
  /** The path component exactly as the caller wrote it, before any URL parser could rewrite it. */
  readonly pathname: string;
  readonly segments: readonly string[];
}

/**
 * Split the path component of a RELATIVE url — everything before the first `?` or `#` — under
 * `splitStrictPathname`'s shape rules, additionally refusing any segment that does not decode to a
 * usable one (`.`, `..`, `%2e%2e`, a malformed escape).
 *
 * A caller that hands its input to `new URL()` MUST run this on the RAW string first. WHATWG
 * parsing resolves dot segments while it builds the URL, so `/u/../tasks` arrives as `/tasks` and
 * the reserved `/u` root that `parseWorkspacePath` refuses is gone before anything can see it.
 */
export function parseRelativePathname(value: string): RelativePathnameParts | null {
  if (typeof value !== 'string') {
    return null;
  }
  const end = value.search(QUERY_OR_FRAGMENT_START);
  const pathname = end === -1 ? value : value.slice(0, end);
  const segments = splitStrictPathname(pathname);
  if (!segments?.every((segment) => decodeSegment(segment) !== null)) {
    return null;
  }
  return { pathname, segments };
}

function matchTemplate(
  template: WorkspaceRouteTemplate,
  segments: readonly string[],
): readonly string[] | null {
  if (template.segments.length !== segments.length) {
    return null;
  }
  const params: string[] = [];
  for (const [index, expected] of template.segments.entries()) {
    if (!expected.startsWith(':')) {
      if (expected !== segments[index]) {
        return null;
      }
      continue;
    }
    const decoded = decodeSegment(segments[index]);
    if (decoded === null) {
      return null;
    }
    params.push(decoded);
  }
  return params;
}

function buildDestination(
  template: WorkspaceRouteTemplate,
  params: readonly string[],
): WorkspaceDestination {
  const id = params[0];
  if (template.family === 'ads-detail') {
    return { family: template.family, section: template.section, key: id };
  }
  const { family } = template;
  if (family === 'person') {
    return { family, personId: id };
  }
  if (family === 'organisation') {
    return { family, organisationId: id };
  }
  if (family === TASK_DETAIL) {
    return { family, taskId: id };
  }
  if (family === 'document-detail') {
    return { family, documentId: id };
  }
  if (family === 'broadcast-builder') {
    return { family, broadcastId: id };
  }
  if (family === 'composition') {
    return { family, projectId: id, compositionId: params[1] };
  }
  return { family };
}

function matchPrefixAlias(segments: readonly string[]): WorkspaceRouteMatch | null {
  for (const alias of WORKSPACE_PREFIX_ALIASES) {
    const matches =
      segments.length >= alias.segments.length &&
      alias.segments.every((segment, index) => segment === segments[index]);
    if (matches) {
      return matched({ family: alias.family }, 'legacy');
    }
  }
  return null;
}

/**
 * Resolve a pathname to a semantic workspace destination. Reserved and unrecognised paths are
 * distinct outcomes so a caller can tell "deliberately not ours" from "we do not know this".
 */
export function parseWorkspacePath(pathname: string): WorkspacePathResult {
  const segments = splitStrictPathname(pathname);
  if (segments === null) {
    return UNKNOWN;
  }
  if (segments.length > 0 && RESERVED_ROOT_SEGMENTS.has(segments[0])) {
    return RESERVED;
  }
  const prefixAlias = matchPrefixAlias(segments);
  if (prefixAlias) {
    return prefixAlias;
  }
  for (const template of WORKSPACE_ROUTE_TEMPLATES) {
    const params = matchTemplate(template, segments);
    if (params !== null) {
      return matched(buildDestination(template, params), template.presentation);
    }
  }
  return UNKNOWN;
}

/** The destination a pathname addresses, or `undefined` when it is reserved or unknown. */
export function workspaceDestinationFor(pathname: string): WorkspaceDestination | undefined {
  const result = parseWorkspacePath(pathname);
  return isMatch(result) ? result.destination : undefined;
}

function convertPath(pathname: string, presentation: WorkspacePresentation): string | null {
  const destination = workspaceDestinationFor(pathname);
  return destination ? workspacePath(destination, presentation) : null;
}

/**
 * The canonical PATHNAME for a workspace path, or `null` when the input is reserved/unknown.
 * Aliases whose identity lives in the query (`/people/organisations`, `/settings/email-accounts`)
 * lose that identity here by design — route those through `normaliseWorkspaceLocation`, which
 * carries the query.
 */
export function toCanonicalWorkspacePath(pathname: string): string | null {
  return convertPath(pathname, 'canonical');
}

/** The permanent `/v2` presentation of a workspace path, or `null` when reserved/unknown. */
export function toCompatibilityWorkspacePath(pathname: string): string | null {
  return convertPath(pathname, 'compatibility');
}

export interface WorkspaceLocationParts {
  readonly pathname: string;
  readonly search?: string;
  readonly hash?: string;
}

export interface NormalisedWorkspaceLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
  readonly changed: boolean;
}

function applyIdentityQuery(
  search: string,
  identity: readonly (readonly [string, string])[],
): string {
  if (identity.length === 0) {
    return search;
  }
  const params = new URLSearchParams(search);
  for (const [key, value] of identity) {
    params.set(key, value);
  }
  const next = params.toString();
  return next.length > 0 ? `?${next}` : '';
}

/**
 * Presentation-only conversion of a location. It rewrites the pathname and nothing else: `hash`
 * always passes through byte-for-byte, and `search` does too UNLESS the alias carries its
 * destination identity in a query key rather than a path segment - dropping `type=organisations`
 * while rewriting `/people/organisations` to `/people` would silently change which records load.
 * On that path the query is re-serialized through `URLSearchParams`, which may normalise encoding
 * and key order; callers MUST NOT rely on byte-stable `search` output. Legacy QUERY DIALECT
 * translation is a separate concern; see `translateLegacyNotificationDestination`.
 *
 * Reserved and unknown locations are returned untouched with `changed: false`.
 */
export function normaliseWorkspaceLocation(
  location: WorkspaceLocationParts,
  presentation: WorkspacePresentation = 'canonical',
): NormalisedWorkspaceLocation {
  const search = location.search ?? '';
  const hash = location.hash ?? '';
  const destination = workspaceDestinationFor(location.pathname);
  const pathname = destination ? workspacePath(destination, presentation) : null;
  if (!destination || pathname === null) {
    return { pathname: location.pathname, search, hash, changed: false };
  }
  const nextSearch = applyIdentityQuery(search, workspaceIdentityQuery(destination));
  return {
    pathname,
    search: nextSearch,
    hash,
    changed: pathname !== location.pathname || nextSearch !== search,
  };
}

/**
 * The pathname as the browser prefers to show it. A writer comparing its own (preferred) target
 * against the live location must run the location through this first: the address bar can still
 * hold a `/v2` or alias input the gate has not replaced yet, and that input names the same
 * destination. Reserved and unknown pathnames come back unchanged.
 */
export function toPreferredWorkspacePath(pathname: string): string {
  return normaliseWorkspaceLocation({ pathname }, surfaceWorkspacePresentation()).pathname;
}

/** Whether two pathnames name the same workspace destination, whatever presentation each uses. */
export function isSameWorkspacePath(left: string, right: string): boolean {
  return toPreferredWorkspacePath(left) === toPreferredWorkspacePath(right);
}

function isFamilyPath(pathname: string, families: readonly WorkspaceRouteFamily[]): boolean {
  const destination = workspaceDestinationFor(pathname);
  return destination !== undefined && families.includes(destination.family);
}

/** Exact — `/v2/ads/advertiser/my-correspondence` is not a correspondence path. */
export function isCorrespondencePath(pathname: string): boolean {
  return isFamilyPath(pathname, ['correspondence']);
}

/** True for the task list and for a task-detail path in either presentation. */
export function isTasksPath(pathname: string): boolean {
  return isFamilyPath(pathname, ['tasks', TASK_DETAIL]);
}

/** True only for a path that addresses a single task — the list is not a detail path. */
export function isTaskDetailPath(pathname: string): boolean {
  return isFamilyPath(pathname, [TASK_DETAIL]);
}

export function isCreativePath(pathname: string): boolean {
  return isFamilyPath(pathname, ['creative']);
}

/** The Ads Explorer index only — an advertiser drill-down is `ads-detail`, not this. */
export function isAdsExplorerPath(pathname: string): boolean {
  return isFamilyPath(pathname, ['ads-explorer']);
}
